'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { id: 'nav_order', label: 'Order Now', href: '/order-placement' },
    { id: 'nav_admin', label: 'Admin', href: '/admin-dashboard' },
    { id: 'nav_production', label: 'Production', href: '/production-schedule' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/90 backdrop-blur-xl shadow-md py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link href="/order-placement" className="flex items-center gap-3 group">
          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
            <Icon name="CakeIcon" size={24} className="text-primary-foreground" />
          </div>
          <span className="text-2xl font-black tracking-tight text-foreground font-heading">
            HoliStromboli
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks?.map((link) => (
            <Link
              key={link?.id}
              href={link?.href}
              className={`text-sm font-semibold uppercase tracking-wider transition-colors ${
                pathname === link?.href
                  ? 'text-primary' :'text-muted-foreground hover:text-primary'
              }`}
            >
              {link?.label}
            </Link>
          ))}
        </nav>

        {/* CTA Button */}
        <Link
          href="/order-placement"
          className="hidden lg:flex items-center gap-2 bg-primary text-primary-foreground px-6 py-2.5 rounded-full text-sm font-bold uppercase tracking-wider hover:bg-primary/90 transition-all hover:scale-105 shadow-lg"
        >
          <Icon name="ShoppingCartIcon" size={18} />
          <span>Order Now</span>
        </Link>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="lg:hidden w-10 h-10 flex flex-col justify-center items-center gap-1.5 group"
          aria-label="Toggle menu"
        >
          <div
            className={`w-6 h-0.5 bg-foreground transition-all ${
              isMenuOpen ? 'rotate-45 translate-y-2' : ''
            }`}
          />
          <div
            className={`w-6 h-0.5 bg-foreground transition-all ${
              isMenuOpen ? 'opacity-0' : ''
            }`}
          />
          <div
            className={`w-6 h-0.5 bg-foreground transition-all ${
              isMenuOpen ? '-rotate-45 -translate-y-2' : ''
            }`}
          />
        </button>
      </div>
      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 w-full bg-white border-t border-border shadow-xl">
          <nav className="flex flex-col p-6 gap-4">
            {navLinks?.map((link) => (
              <Link
                key={link?.id}
                href={link?.href}
                onClick={() => setIsMenuOpen(false)}
                className={`text-base font-semibold uppercase tracking-wider py-3 px-4 rounded-lg transition-colors ${
                  pathname === link?.href
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                {link?.label}
              </Link>
            ))}
            <Link
              href="/order-placement"
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center justify-center gap-2 bg-secondary text-secondary-foreground px-6 py-3 rounded-full text-sm font-bold uppercase tracking-wider mt-4"
            >
              <Icon name="ShoppingCartIcon" size={18} />
              <span>Order Now</span>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}